<script setup lang="ts">
import type { BadgeProps } from '/#/components/base/badge'
import type { IconColors, IconName, IconProps } from '/#/components/base/icon/icon'
import type { ComputedRef } from 'vue'

import isUndefined from 'lodash-es/isUndefined'

import { isEmpty } from '/@/utils/is'

const props = withDefaults(defineProps<BadgeProps>(), {
  label: undefined,
  theme: undefined,
  icon: undefined,
  color: 'secondary',
  priority: undefined,
  size: 'xs',
  variant: 'soft',
  infoTip: undefined,
})

/**
 * @var icon
 * @description Makes icon props easier to use. We don't need to size every time.
 * If only the icon name is defined, it will automatically be "size: small".
 */
const icon: ComputedRef<IconProps | undefined> = computed(() => {
  if (props.withSeverityBar && props.priority)
    return {
      name: `severity-bar-${props.priority.toLowerCase()}` as IconName,
      size: 'sm',
      color: 'inherit',
    } as IconProps

  if (typeof props.icon === 'string')
    return { name: props.icon, size: 'sm', color: 'inherit' } as IconProps

  return props.icon as IconProps
})

/**
 * @var label
 * @description If a charLimit is defined and the label is longer than this limit, it truncates the label.
 * Otherwise, it returns the label (string | number).
 */
const label: ComputedRef<string | number | undefined> = computed(() => {
  if (typeof props.label !== 'string') return props.label

  const isLabelNeedEllipsis = props.charLimit && props.label.length > props.charLimit

  if (isLabelNeedEllipsis) {
    const croppedLabel = props.label.substring(0, props.charLimit)

    return `${croppedLabel}...`
  }

  return props.label
})

/**
 * @var appearance
 * @description It sets how the badge will appear according to incoming props.
 */
const appearance = computed(() => [
  props.color,
  props.size,
  props.variant,
  props.priority?.toLowerCase(),
  props.border && 'badge-border',
  props.rounded && 'badge-rounded',
  props.withSeverityBar && 'with-severity-bar',
  !isEmpty(label.value) && !props.withSeverityBar && 'gap-1.5', // There is too much space between the bar and the text, the gap should be 1.5 in all cases except the bar.
  props.withSeverityBar && label.value && 'max-h-6 gap-0', // We need to define max-height, because the severity bar, expenses the height
  props.circle && 'badge-circle',
  props.withDot && !props.icon && 'with-dot',
  props.theme && `theme-${props.theme}`,
])
</script>

<template>
  <div v-tippy="tippy" class="badge" :class="appearance">
    <span v-if="!!icon || withDot">
      <Icon v-if="icon" v-bind="icon" class="icon" />
    </span>

    <slot>
      <span v-if="label !== undefined" class="badge-text">
        {{ label }}
      </span>
      <InformationIcon
        v-if="!isUndefined(infoTip)"
        :content="infoTip.content"
        :icon-color="color as IconColors"
      />
    </slot>
  </div>
</template>
